import {
  productTypeArrayToAnalyticsProductTypeArray,
  productTypeToAnalyticsProductType,
} from '#src/utils';

export type TrackingEvent = keyof typeof trackingDictionary;
export type TrackingFunctionEvent = keyof typeof trackingFunctionDictionary;
export type TrackingPageEvent = keyof typeof trackingPagesDictionary;
export type ProductTypesToTrack = string;

export type AnalyticsPayload = {
  category: string;
  action: string;
  label: string;
};

export type AnalyticsFunctionPayload = (productType: ProductTypesToTrack) => {
  category: string;
  action: string;
  label: string;
};

const TRACKING_PRODUCT_TYPES_JOINER = '-';

export const concatAnalyticsProductTypes = (
  productTypes: ProductTypesToTrack[]
) =>
  productTypeArrayToAnalyticsProductTypeArray(productTypes).join(
    TRACKING_PRODUCT_TYPES_JOINER
  );

export const trackingPagesDictionary = {
  onPaymentPageLoad: (productTypes: ProductTypesToTrack[]) => ({
    env_template: 'payment',
    page: `/PB/${concatAnalyticsProductTypes(productTypes)}/payment/`,
  }),
  onConfirmationPaymentPageLoad: (productTypes: ProductTypesToTrack[]) => ({
    env_template: 'confirmation',
    page: `/PB/${concatAnalyticsProductTypes(productTypes)}/confirmation/`,
  }),
} as const;

export const trackingDictionary = {
  onPriceBreakdownOpenClick: {
    action: 'shopping_basket_payment_pricebreak',
    label: 'pricebreak_clicks_button_open',
  },
  onPriceBreakdownCloseClick: {
    action: 'shopping_basket_payment_popup',
    label: 'pricebreak_clicks_button_close',
  },
  onPriceBreakdownLoad: {
    action: 'shopping_basket_payment_popup',
    label: 'appearances_pricebreak',
  },
  onCheckoutClick: {
    action: 'shopping_basket_payment',
    label: 'purchase_clicks_PB',
  },
  onTermsAndConditionsClick: {
    action: 'shopping_basket_payment',
    label: 'conditions_clicks_button',
  },
  onPrivacyPolicyClick: {
    action: 'shopping_basket_payment',
    label: 'policy_clicks_button',
  },
  onSuccessModalLoad: {
    action: 'shopping_basket_payment_modal',
    label: `appearances_success`,
  },
  onSuccessModalButtonClick: {
    action: 'shopping_basket_payment_modal',
    label: `success_clicks_button_ok`,
  },
} as const;

export const trackingFunctionDictionary = {
  onProductTypeLoad: (productType: ProductTypesToTrack) => ({
    action: 'shopping_basket_payment',
    label: `appearances_payment_${productTypeToAnalyticsProductType(
      productType
    )}`,
  }),
  onCheckoutProductTypeClick: (productType: ProductTypesToTrack) => ({
    action: 'shopping_basket_payment',
    label: `purchase_clicks_cat:${productTypeToAnalyticsProductType(
      productType
    )}`,
  }),
} as const;

export const getAnalyticsPayload = (
  aTrackingEvent: TrackingEvent | TrackingFunctionEvent
) => {
  return (
    trackingDictionary[aTrackingEvent] ||
    trackingFunctionDictionary[aTrackingEvent]
  );
};

export const getAnalyticsPagePayload = (aTrackingEvent: TrackingPageEvent) => {
  return trackingPagesDictionary[aTrackingEvent];
};

export const isAnalyticsPayload = (
  payload: AnalyticsPayload | AnalyticsFunctionPayload
): payload is AnalyticsPayload => {
  return typeof payload === 'object';
};

export const isAnalyticsFunctionPayload = (
  payload: AnalyticsPayload | AnalyticsFunctionPayload
): payload is AnalyticsFunctionPayload => {
  return typeof payload === 'function';
};
